'use client';

import { useState } from 'react';
import { Modal } from '@/core/components/ui/modal';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { toast } from '@/core/utils/toast';
import { fetchAPI } from '@/core/api/client';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { maskAmountInput } from '@/core/utils/currency';

interface NegotiatePriceModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: number;
    catalogPrice: number;
    currency: string;
    onSuccess?: () => void;
    /** Quantity picked via a product-quantity block, if any (defaults to 1). The
     * negotiation backend has no quantity field of its own, so this is folded into
     * the free-text note the admin already reads when reviewing an offer — good
     * enough to communicate "this is a bulk order", without a schema change. */
    quantity?: number;
}

export function NegotiatePriceModal({ isOpen, onClose, productId, catalogPrice, currency, onSuccess, quantity = 1 }: NegotiatePriceModalProps) {
    const { t } = useTranslation();
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        const parsed = parseFloat(amount.replace(',', '.'));
        if (isNaN(parsed) || parsed <= 0) {
            toast.error(t('negotiation.invalid_amount', 'Podaj prawidłową cenę'));
            return;
        }
        if (parsed >= catalogPrice) {
            toast.error(t('negotiation.amount_too_high', 'Proponowana cena musi być niższa od ceny sklepowej'));
            return;
        }
        setSubmitting(true);
        try {
            const quantityPrefix = quantity > 1 ? `Ilość: ${quantity} szt.` : '';
            const fullNote = [quantityPrefix, note.trim()].filter(Boolean).join(' — ');
            await fetchAPI('/negotiations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: productId, amount: parsed, note: fullNote || undefined }),
            });
            toast.success(t('negotiation.offer_sent', 'Propozycja wysłana! Sprawdź status w Moim koncie.'));
            setAmount('');
            setNote('');
            onSuccess?.();
            onClose();
        } catch (e: any) {
            toast.error(e?.message || t('negotiation.offer_error', 'Nie udało się wysłać propozycji'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('negotiation.propose_price_title', 'Zaproponuj cenę')}>
            <div className="space-y-4">
                <p className="text-sm text-slate-600">
                    {t('negotiation.catalog_price_label', 'Cena sklepowa')}: <strong>{catalogPrice.toFixed(2)} {currency}</strong>
                    {quantity > 1 && <> · {t('negotiation.quantity_label', 'Ilość')}: <strong>{quantity} szt.</strong></>}
                </p>
                <div className="space-y-2">
                    <Label htmlFor="negotiateAmount">{t('negotiation.your_price_label', 'Twoja propozycja')}</Label>
                    <Input
                        id="negotiateAmount"
                        type="text"
                        inputMode="decimal"
                        maxLength={10}
                        value={amount}
                        onChange={(e) => setAmount(maskAmountInput(e.target.value))}
                        placeholder={`np. ${(catalogPrice * 0.9).toFixed(2)}`}
                        className="h-11 rounded-xl"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="negotiateNote">{t('negotiation.note_label', 'Wiadomość (opcjonalnie)')}</Label>
                    <Input
                        id="negotiateNote"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t('negotiation.note_placeholder', 'np. kupię od razu, jeśli...')}
                        className="h-11 rounded-xl"
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel', 'Anuluj')}</Button>
                    <Button type="button" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? t('negotiation.sending', 'Wysyłanie...') : t('negotiation.send_offer', 'Wyślij propozycję')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
